import React, { useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Button, Card, CardActions, CardContent, CardHeader, Chip, Collapse, Divider, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { generateHexFromString } from '@/_utils/colorUtils';
import { TabBadge } from '@/components/_common';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppSelector } from '@/redux/hooks';
import { selectGroupedVersionSelections, selectVersionKeys, selectVersionSelections } from '@/redux/selectors/orderPlacementSelectors';
import { VersionGroupedByParent } from '@/redux/slices/slice_types';
import OPSortedFlatVersions from '../OPSortedFlatVersions';
import OPVersionSelectionsFlatFromLineage from './OPVersionSelectionsFlatFromLineage';
import OPVersionSelectionsListFromLineage from './OPVersionSelectionsListFromLineage';
import Feature from './Feature';
import PolarisIcon from '../../../_common/PolarisIcon';
import { isObjectAVersion } from '../OPVersionSelections/utils';
import { getVersionFromGroup } from '@/_utils/bulkSearchUtils';
import { ConditionalTooltip } from '@/exports/columnCommonComponents';

function extractKeys(obj: VersionGroupedByParent) {
  let keys: number[] = [];
  for (const key in obj) {
    if (key.startsWith('_')) {
      keys.push(parseInt(key.replace('_', '')));
      if (typeof obj[key] === 'object') {
        keys = keys.concat(extractKeys(obj[key]));
      }
    }
  }
  return Array.from(new Set(keys));
}

const OPVersionSelectionsCSV = () => {
  const selectedVersions = useAppSelector(selectVersionSelections);
  const groupedByParent = useAppSelector(selectGroupedVersionSelections);
  const versionsByKey = useAppSelector(selectVersionKeys);
  const versionColors = useContext(ConfigContext)?.versionColors || [];
  const [expanded, setExpanded] = useState('');
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  const selectTitleById = (id: string) => {
    const titleKey = String(id).substring(1);
    return versionsByKey[titleKey] || {};
  };

  const toggleCollapse = (parentKey: string) => {
    setExpanded(expanded === parentKey ? '' : parentKey);
  };

  const renderVersionTypeChips = (versions: VersionGroupedByParent) => {
    // group the version type and count the number of versions per each
    const versionTypeGroup: Record<string, number> = {};

    // Helper function to process a single version
    const processVersion = (versionId: string) => {
      const version = selectTitleById(versionId);
      if (version?.entity_type === 'version') {
        const versionType: string = version.version_type || version.name;
        if (versionType) {
          versionTypeGroup[versionType] = (versionTypeGroup[versionType] ?? 0) + 1;
        }
      }
    };

    // Helper function to recursively process version structure
    const processVersions = (versionObj: VersionGroupedByParent | string) => {
      if (typeof versionObj === 'string') {
        processVersion(versionObj);
        return;
      }

      // Handle single version object directly
      if (isObjectAVersion(versionObj) && versionObj?.entity_type === 'version') {
        const versionType = versionObj.version_type || versionObj.name;
        if (versionType) {
          versionTypeGroup[versionType] = (versionTypeGroup[versionType] ?? 0) + 1;
        }
        return;
      }

      if (typeof versionObj === 'object' && versionObj !== null) {
        Object.keys(versionObj).forEach((key) => {
          processVersions(versionObj[key]);
        });
      }
    };

    // Process the versions structure
    processVersions(versions);

    return Object.keys(versionTypeGroup)
      .sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()))
      .map((typeKey) => {
        const color = versionColors[typeKey.toLowerCase()] || generateHexFromString(typeKey);
        return (
          <Chip
            key={typeKey}
            label={
              <ConditionalTooltip show title={typeKey} arrow>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                  <Typography noWrap sx={{ maxWidth: 150 }}>
                    {typeKey.toUpperCase()}
                  </Typography>
                  <TabBadge count={versionTypeGroup[typeKey]} />
                </Box>
              </ConditionalTooltip>
            }
            icon={<PolarisIcon icon="check" height={15} style={{ color: theme.palette.background.default }} />}
            sx={{
              px: 0,
              minWidth: 80,
              alignItems: 'center',
              border: '1px solid',
              borderRadius: 5,
              color: 'background.default',
              borderColor: color,
              '&, &.MuiChip-root.MuiChip-filled': {
                backgroundColor: color
              },
              '&, &.MuiChip-root:not(.MuiChip-clickable).MuiChip-outlined': {
                backgroundColor: color
              },
              '& .MuiBadge-root': {
                backgroundColor: 'background.paper',
                color
              }
            }}
          />
        );
      });
  };

  // if we don't have a hierarchy, fake it by grouping by series/season/etc
  if (selectedVersions?.length && Object.keys(selectedVersions[0]).includes('piece_id')) return <OPSortedFlatVersions />;

  useEffect(() => {
    if (!loading) return;
    const allKeys = extractKeys(groupedByParent);
    const fetchedVersionKeys = Object.keys(versionsByKey);
    // find any keys from allKeys that do not exist in fetchedVersionKeys
    const missingVersionKeys = allKeys.filter((key) => !fetchedVersionKeys.includes(String(key)));
    if (missingVersionKeys.length === 0 || (missingVersionKeys.length === 1 && missingVersionKeys[0] === 0)) {
      setLoading(false);
      return;
    }
  }, [versionsByKey]);

  return (
    <Container>
      {loading ? (
        'Loading...'
      ) : (
        <>
          {Object.keys(groupedByParent).map((parentKey) => {
            const version = getVersionFromGroup(groupedByParent, parentKey);
            const children = groupedByParent[parentKey];

            if (version?.lineage_meta?.feature_name) {
              return <Feature key={parentKey} featureName={version.lineage_meta.feature_name} entityType={version?.entity_type as string} featureChildren={children} parentKey={parentKey} />;
            }

            // Series and/or Season
            return (
              <Card key={parentKey} elevation={0}>
                <CardHeader
                  sx={{ p: 0, pt: 1, pl: 1.5 }}
                  title={
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                      {version?.lineage_meta?.series_name ?? version?.lineage_meta?.feature_name}
                    </Typography>
                  }
                />
                <CardContent sx={{ pb: 0, pt: 1, pl: 1.5, pr: 1, maxHeight: 500, overflowY: 'scroll' }}>
                  <Collapse in={expanded === parentKey}>
                    <OPVersionSelectionsListFromLineage selectedVersions={children} />
                  </Collapse>

                  <Collapse in={expanded !== parentKey}>
                    <OPVersionSelectionsFlatFromLineage selectedVersions={children} />
                  </Collapse>

                  <Divider sx={{ mt: 2, mb: 1 }} />
                </CardContent>
                <CardActions sx={{ pr: 2 }}>
                  <Grid container justifyContent="space-between" alignItems="center">
                      <Grid item>
                        <Button variant="text" onClick={() => toggleCollapse(parentKey)}>
                          <PolarisIcon icon="right" height={20} style={{ transform: expanded === parentKey ? 'rotate(-90deg)' : '' }} />
                          {expanded === parentKey ? 'See Less' : 'See More'}
                        </Button>
                      </Grid>
                    <Grid item sx={{ flex: 'none' }}>
                      <Grid
                        container
                        flexWrap="wrap"
                        columnGap={1}
                        rowGap={1}
                        justifyContent={'flex-end'}
                      >
                        {expanded !== parentKey && renderVersionTypeChips(children)}
                      </Grid>
                    </Grid>
                  </Grid>
                </CardActions>
              </Card>
            );
          })}
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(2)};
`;

export default OPVersionSelectionsCSV;
